import type { FC } from "react";
import { Box, Button, Grid, Space, Stack, Text, Title } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useParams } from "react-router-dom";
import {
  AddressDisplay,
  getAllOrderComments,
  getAllRedoComments,
  getOrderStatusStep,
  getPatientLabelFirstLine,
  getPatientLabelSecondLine,
  ItemsDescription,
  Rdv2ContinueBanner,
} from "@jasper/shared";
import { OrderStatusStepper } from "/@/pages/orders/OrderStatusStepper";
import { OrderDesignBanner } from "/@/components/OrderDesignBanner";
import type { Order } from "/@/generated/graphql";
import { useOrderPage } from "./useOrderPage";
import { getDisplayedProducts } from "./order-page.utils";
import { OrderPageModals } from "./OrderPageModals";
import { OrderRequestModals } from "./OrderRequestModals";
import { OrderPageActions } from "./OrderPageActions";
import { OrderCommentsList } from "./OrderCommentsList";
import {
  hasOrderDesignToDisplay,
  OrderDesignContent,
} from "./OrderDesignContent";
import { OrderSelectedProductArches } from "./OrderSelectedProductArches";

export const OrderPageV1: FC = () => {
  const { id } = useParams();
  const { classes } = useStyles();
  const state = useOrderPage(id!);
  const {
    order,
    selectedProduct,
    setSelectedProduct,
    orderComments,
    expectedDeliveryDate,
    permissions,
    modals,
    actions,
  } = state;

  if (!order || !expectedDeliveryDate) {
    return null;
  }

  const user = order.user;
  const hasDesign = hasOrderDesignToDisplay(state);
  const redoComments = getAllRedoComments(order);

  return (
    <Box
      p="lg"
      style={() => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
      })}
    >
      <OrderPageModals state={state} />
      <OrderRequestModals state={state} />
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Title
            order={4}
            style={theme => ({
              color: theme.colors.primary[4],
            })}
          >
            Commande {order.orderReference}
          </Title>
        </Box>
        <Box
          style={theme => ({
            display: "flex",
            gap: theme.spacing.sm,
          })}
        >
          <OrderPageActions state={state} />
        </Box>
      </Box>
      <Space h="sm" />
      {state.showRdv2ContinueBanner && (
        <Rdv2ContinueBanner order={order} onSubmit={actions.setOrderToDraft} />
      )}
      <OrderDesignBanner orderId={order.id} />

      <Grid
        gap="xl"
        style={{
          flexGrow: 1,
          minHeight: "fit-content",
          maxHeight: "30%",
        }}
      >
        <Grid.Col
          className={classes.gridColContainer}
          span={5}
          style={{
            height: "inherit",
          }}
        >
          <Box className={classes.itemsDetails}>
            <Title order={5}>Patient</Title>
            <Text className={classes.boxText}>
              {getPatientLabelFirstLine(order)}
            </Text>
            <Text className={classes.boxText}>
              {getPatientLabelSecondLine(order)}
            </Text>
            <Space h="md" />
            <Title order={5}>Praticien</Title>
            <Text className={classes.boxText}>
              {user.firstName} {user.lastName}
            </Text>
            <Text className={classes.boxText}>
              <AddressDisplay address={user.userGroup?.addresses?.[0]} />
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col
          className={classes.gridColContainer}
          span={7}
          style={{
            height: "inherit",
          }}
        >
          <Box className={classes.itemsDetails}>
            <Space h="md" />
            <Title order={5}>
              Date de livraison estimée:{" "}
              {expectedDeliveryDate.toLocaleDateString()} &nbsp;
              {permissions.canShowCarrierDeliveryDetails && (
                <Button onClick={modals.carrierDelivery.open}>
                  Voir les détails de livraison
                </Button>
              )}
            </Title>
            <Space h="md" />
            <OrderStatusStepper
              orderStatus={getOrderStatusStep(order.status)}
              order={order as unknown as Order}
              labelsUnder={true}
              grow={true}
            />
          </Box>
        </Grid.Col>
      </Grid>
      <Space h="xl" />
      <Grid gap="xl" style={{ flexGrow: 1 }}>
        <Grid.Col
          className={classes.gridColContainer}
          order={hasDesign ? 3 : 1}
          span={5}
          style={{
            height: "inherit",
          }}
        >
          <Box
            className={classes.itemsDetails}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Title order={4} className={classes.boxTitle}>
              Dents du produit sélectionné
            </Title>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <OrderSelectedProductArches selectedProduct={selectedProduct} />
            </Box>
          </Box>
        </Grid.Col>

        <Grid.Col
          className={classes.gridColContainer}
          span={7}
          order={2}
          style={{
            height: "inherit",
          }}
        >
          <Box className={classes.itemsDetails} style={{ height: "100%" }}>
            <Title order={4} className={classes.boxTitle}>
              {`Détail de la commande${
                order.products.length > 1
                  ? " (cliquez sur un produit pour le sélectionner)"
                  : ""
              }`}
            </Title>
            <Box>
              <Space h="md" />
              <Box>
                {getDisplayedProducts(order.products).map(product => (
                  <Box key={product.id}>
                    <Button
                      variant={
                        selectedProduct?.id === product.id
                          ? "filled"
                          : "outline"
                      }
                      styles={{
                        inner: {
                          justifyContent: "start",
                        },
                      }}
                      color={
                        selectedProduct?.id === product.id
                          ? "primary.5"
                          : undefined
                      }
                      style={theme => ({
                        "&:hover": {
                          backgroundColor:
                            selectedProduct?.id === product.id
                              ? undefined
                              : theme.colors.greyScale[9],
                        },
                      })}
                      h="auto"
                      p={8}
                      mb="xs"
                      w="100%"
                      onClick={() => setSelectedProduct(product)}
                    >
                      <ItemsDescription
                        product={product}
                        order={order}
                        shouldTranslate={false}
                      />
                    </Button>
                  </Box>
                ))}
              </Box>
              <Space h="xl" />
              <Title order={5}>Notes du praticien sur la commande</Title>
              <div
                style={{
                  padding: "10px",
                }}
              >
                <OrderCommentsList
                  comments={getAllOrderComments(order)}
                  orderComments={orderComments}
                  getPresignedDownloadUrl={actions.getPresignedDownloadUrl}
                  emptyLabel="Pas de note pour cette commande"
                />
              </div>
              {redoComments.length > 0 && (
                <>
                  <Space h="xl" />
                  <Title order={5}>
                    Notes du praticien sur la demande de refabrication de cette
                    commande
                  </Title>
                  <div
                    style={{
                      padding: "10px",
                    }}
                  >
                    <OrderCommentsList
                      comments={redoComments}
                      orderComments={orderComments}
                      getPresignedDownloadUrl={actions.getPresignedDownloadUrl}
                    />
                  </div>
                </>
              )}
            </Box>
          </Box>
        </Grid.Col>

        {hasDesign && (
          <Grid.Col
            className={classes.gridColContainer}
            order={1}
            span={5}
            style={{
              aspectRatio: "1 / 0.75",
            }}
          >
            <Stack className={classes.itemsDetails} gap="md">
              <Title order={4} className={classes.boxTitle}>
                Aperçu de la conception
              </Title>
              <OrderDesignContent state={state} />
            </Stack>
          </Grid.Col>
        )}
      </Grid>
    </Box>
  );
};

const useStyles = createStyles(theme => ({
  gridColContainer: {
    height: "100%",
  },
  itemsDetails: {
    borderRadius: "10px",
    outline: `1px solid ${theme.colors.greyScale[9]}`,
    padding: theme.spacing.md,
    height: "100%",
    minHeight: "100%",
    minWidth: "100%",
  },
  boxTitle: {
    color: theme.colors.primary[4],
  },
  boxText: {
    fontSize: theme.fontSizes.sm,
  },
}));
